import React, { useState, useEffect } from 'react';
import { API_URL } from './utils/authUtils';

const AdminWorkdayConfiguration = () => {
  const [companies, setCompanies] = useState([]);
  const [workdayConfigs, setWorkdayConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    workday_tenant: '',
    client_id: '',
    client_secret: '',
    workday_base_domain: 'wd2-impl-services1.workday.com',
    custom_authorization_url: '',
    custom_token_url: '',
    custom_api_base_url: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Fetch companies first
      console.log('Fetching companies from', `${API_URL}/api/companies`);
      let companiesResponse;
      try {
        companiesResponse = await fetch(`${API_URL}/api/companies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('Companies response status:', companiesResponse.status);
        
        const companiesText = await companiesResponse.text();
        console.log('Companies raw response:', companiesText);
        
        if (!companiesResponse.ok) {
          throw new Error(`Companies API failed: ${companiesResponse.status} - ${companiesText}`);
        }
        
        const companiesData = JSON.parse(companiesText);
        console.log('Companies parsed data:', companiesData);
        setCompanies(companiesData.companies || []);
      } catch (companiesError) {
        console.error('Companies fetch error:', companiesError);
        setError('Failed to load companies: ' + companiesError.message);
        return; // Don't continue if companies failed
      }

      // Fetch Workday configurations
      console.log('Fetching Workday configs from', `${API_URL}/api/admin/workday/configurations`);
      let configsResponse;
      try {
        configsResponse = await fetch(`${API_URL}/api/admin/workday/configurations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('Configs response status:', configsResponse.status);
        
        const configsText = await configsResponse.text();
        console.log('Configs raw response:', configsText);
        
        if (!configsResponse.ok) {
          throw new Error(`Workday configs API failed: ${configsResponse.status} - ${configsText}`);
        }
        
        const configsData = JSON.parse(configsText);
        console.log('Configs parsed data:', configsData);
        setWorkdayConfigs(configsData.configurations || []);
      } catch (configsError) {
        console.error('Configs fetch error:', configsError);
        setError('Failed to load Workday configurations: ' + configsError.message);
        return;
      }

    } catch (error) {
      console.error('fetchData general error:', error);
      setError('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const isEdit = selectedConfig !== null;
      const url = isEdit 
        ? `${API_URL}/api/admin/workday/configurations/${selectedConfig.id}`
        : `${API_URL}/api/admin/workday/configurations`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(isEdit ? 'Configuration updated successfully!' : 'Configuration created successfully!');
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
        await fetchData(); // Refresh data
      } else {
        setError(result.detail || 'Failed to save configuration');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this Workday configuration?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/workday/configurations/${configId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Configuration deleted successfully!');
        await fetchData();
      } else {
        const result = await response.json();
        setError(result.detail || 'Failed to delete configuration');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    }
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setFormData({
      company_id: config.company_id,
      workday_tenant: config.workday_tenant,
      client_id: config.client_id,
      client_secret: '', // Don't populate for security
      workday_base_domain: config.workday_base_domain,
      custom_authorization_url: config.custom_authorization_url || '',
      custom_token_url: config.custom_token_url || '',
      custom_api_base_url: config.custom_api_base_url || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      workday_tenant: '',
      client_id: '',
      client_secret: '',
      workday_base_domain: 'wd2-impl-services1.workday.com',
      custom_authorization_url: '',
      custom_token_url: '',
      custom_api_base_url: ''
    });
    setSelectedConfig(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'configured':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'pending':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getCompaniesWithStatus = () => {
    return companies.map(company => {
      const config = workdayConfigs.find(c => c.company_id === company.id);
      return {
        ...company,
        workday_config: config,
        has_workday: !!config
      };
    });
  };

  const availableCompanies = companies.filter(company => 
    !workdayConfigs.some(config => config.company_id === company.id)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-gray-600">Loading companies and configurations...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <svg className="mr-3 text-blue-600 w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Company Workday Management
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Configure Workday integrations for recruiting partners
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Workday Config
          </button>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        {/* Companies Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Companies ({getCompaniesWithStatus().length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Workday Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Integration ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {getCompaniesWithStatus().map((company) => (
                  <tr key={company.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {company.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {company.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {company.has_workday ? (
                          <>
                            {getStatusIcon(company.workday_config.setup_status)}
                            <span className="ml-2 text-sm text-gray-900 dark:text-white capitalize">
                              {company.workday_config.setup_status}
                            </span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="ml-2 text-sm text-gray-500">Not configured</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {company.workday_config?.integration_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {company.workday_config?.workday_tenant || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {company.workday_config?.updated_at ? 
                        new Date(company.workday_config.updated_at).toLocaleDateString() : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {company.has_workday ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(company.workday_config)}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                            title="Edit Configuration"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(company.workday_config.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400"
                            title="Delete Configuration"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Create Workday Configuration
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-300"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company
                  </label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select a company...</option>
                    {availableCompanies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Workday Tenant
                  </label>
                  <input
                    type="text"
                    value={formData.workday_tenant}
                    onChange={(e) => setFormData({...formData, workday_tenant: e.target.value})}
                    placeholder="e.g., company_gms1"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={formData.client_id}
                      onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value={formData.client_secret}
                      onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Workday Base Domain
                  </label>
                  <input
                    type="text"
                    value={formData.workday_base_domain}
                    onChange={(e) => setFormData({...formData, workday_base_domain: e.target.value})}
                    placeholder="e.g., wd2-impl-services1.workday.com"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Custom URL Overrides Section */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Custom URL Overrides (Optional)
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom Authorization URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_authorization_url}
                        onChange={(e) => setFormData({...formData, custom_authorization_url: e.target.value})}
                        placeholder="Leave empty to use generated URL"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom Token URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_token_url}
                        onChange={(e) => setFormData({...formData, custom_token_url: e.target.value})}
                        placeholder="Leave empty to use generated URL"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom API Base URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_api_base_url}
                        onChange={(e) => setFormData({...formData, custom_api_base_url: e.target.value})}
                        placeholder="Leave empty to use generated URL"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md dark:bg-gray-600 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Create Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal (similar structure to create modal) */}
        {showEditModal && selectedConfig && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Edit Workday Configuration - {companies.find(c => c.id === selectedConfig.company_id)?.name}
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-300"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company
                  </label>
                  <input
                    type="text"
                    value={companies.find(c => c.id === selectedConfig.company_id)?.name || ''}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm dark:bg-gray-600 dark:border-gray-600 dark:text-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Workday Tenant
                  </label>
                  <input
                    type="text"
                    value={formData.workday_tenant}
                    onChange={(e) => setFormData({...formData, workday_tenant: e.target.value})}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={formData.client_id}
                      onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value={formData.client_secret}
                      onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                      placeholder="Leave empty to keep current secret"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to keep existing secret</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Workday Base Domain
                  </label>
                  <input
                    type="text"
                    value={formData.workday_base_domain}
                    onChange={(e) => setFormData({...formData, workday_base_domain: e.target.value})}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Custom URL Overrides Section */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Custom URL Overrides (Optional)
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom Authorization URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_authorization_url}
                        onChange={(e) => setFormData({...formData, custom_authorization_url: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom Token URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_token_url}
                        onChange={(e) => setFormData({...formData, custom_token_url: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Custom API Base URL
                      </label>
                      <input
                        type="url"
                        value={formData.custom_api_base_url}
                        onChange={(e) => setFormData({...formData, custom_api_base_url: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md dark:bg-gray-600 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Update Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWorkdayConfiguration;